import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { StockLocationRoleAssignmentContext } from '../../../../../context/StockLocationRoleAssignmentContext';
import { TagRelationContext } from '../../../../../context/TagRelationContext';
import { StockLocation } from '../../../../../types/stock_location';
import { StockLocationRole } from '../../../../../types/stock_location_role_assignment';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../types/translation/Translator';
import { countries } from '../../../../../util/country';
import { Routing } from '../../../../../util/routes.util';
import { StockLocationFilter } from '../../../../../VentoryUI/components/filters/StockLocationFilter';
import { CreateButton, ImportIconButton } from '../../../../components/Button';
import { SearchFilter } from '../../../../components/Filter';
import Table, { TableHeader } from '../../../../components/Table';
import { DeleteIconButton, SettingsIconButton } from '../../../../icons';
import StockLocationDeleteModal from './StockLocationDeleteModal';
import StockLocationImportModal from './StockLocationImportModal';
import { CompanyContext } from '../../../../../context/CompanyContext';

interface DeleteModalProps {
  open: boolean;
  stockLocation: StockLocation | undefined;
}

export default function StockLocationOverviewPane() {
  const navigate = useNavigate();

  const { isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { hasStockLocationRoleInCompany } = useContext(StockLocationRoleAssignmentContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { currentCompany, companies } = useContext(CompanyContext);

  const allItems = useMemo(() => {
    return [...stockLocations.values()].filter(
      sl =>
        currentCompany.partner ||
        hasStockLocationRoleInCompany(sl.companyId, StockLocationRole.STOCK_LOCATION_VIEWER, sl.id),
    );
  }, [stockLocations]);

  const [items, setItems] = useState(allItems);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [deleteModalProps, setDeleteModalProps] = useState<DeleteModalProps>({ open: false, stockLocation: undefined });

  const headers: TableHeader<StockLocation>[] = [
    ...(currentCompany.partner
      ? [
          {
            key: 'company',
            name: translate({ key: TranslationKey.company }),
            text: (item: StockLocation) => companies.get(item.companyId)?.name || '',
          },
        ]
      : []),
    { key: 'name', name: translate({ key: TranslationKey.name }), text: item => item.name },
    { key: 'identifier', name: translate({ key: TranslationKey.identifier }), text: item => item.identifier },
    { key: 'city', name: translate({ key: TranslationKey.city }), text: item => item.address?.city || '' },
    {
      key: 'country',
      name: translate({ key: TranslationKey.country }),
      text: item => countries.find(c => c.cca2 === item.address?.countryCode)?.name.common || '',
    },
  ];

  if (isAdmin) {
    headers.push({
      key: 'delete',
      name: '',
      text: stockLocation => <DeleteIconButton onClick={event => setDeleteModalProps({ open: true, stockLocation })} />,
    });
  }

  const filter = new StockLocationFilter(tagRelations, currentCompany);

  return (
    <div className='flex h-full flex-col gap-4'>
      {importModalOpen && <StockLocationImportModal open={importModalOpen} setOpen={setImportModalOpen} />}
      {deleteModalProps.open && deleteModalProps.stockLocation && (
        <StockLocationDeleteModal
          open={deleteModalProps.open}
          setOpen={open => setDeleteModalProps({ ...deleteModalProps, open })}
          stockLocation={deleteModalProps.stockLocation}
        />
      )}

      <div className='grid gap-2'>
        <p className='page-title flex-grow overflow-hidden text-ellipsis'>
          {translate({ key: TranslationKey.stockLocationSettingsScreenTitle })}
        </p>
        <p className='subtitle'>{translate({ key: TranslationKey.stockLocationSettingsScreenSubtitle })}</p>
      </div>

      <div className='flex justify-between'>
        <SearchFilter items={allItems} setItems={setItems} filter={filter} />
        {isAdmin && (
          <div className='flex items-center gap-1'>
            <CreateButton onClick={() => navigate(Routing.settings.stockLocations.create())} />
            <ImportIconButton onClick={() => setImportModalOpen(true)} />
            <SettingsIconButton
              onClick={() => navigate(Routing.settings.stockLocations.advanced.importConfigurations.overview())}
            />
          </div>
        )}
      </div>

      <Table
        filter={filter}
        items={items}
        headers={headers}
        onClick={item =>
          (currentCompany.partner ||
            hasStockLocationRoleInCompany(item.companyId, StockLocationRole.STOCK_LOCATION_MANAGER, item.id)) &&
          navigate(Routing.settings.stockLocations.update(item.id).locationDetails())
        }
      />
    </div>
  );
}
